import { Button, Container } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  PiCalendar,
  PiCalendarMinus,
  PiCalendarPlus,
  PiDeviceMobile,
  PiGear,
  PiPackage,
  PiTestTube,
} from 'react-icons/pi';
import {
  DEMO_MODE,
  IAccountMutationForm,
  ISRC_GENERATION_MODE,
} from 'services/accounts/types';
import { CLIENT_ACCESS_MODE, CURRENCY } from 'services/types';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';

const LabelSettings = () => {
  const { values, setFieldValue, status } =
    useFormikContext<IAccountMutationForm>();
  const { t } = useTranslation();
  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <div className='flex gap-6 justify-start w-full'>
      <div className='flex flex-col gap-6 justify-start w-[350px]'>
        <Container className='self-start w-full max-w-[350px]'>
          <Container.Header
            title={
              <>
                <PiCalendar /> {t('input.label.period')}
              </>
            }
          />
          <Container.Content>
            <FormInput.Text
              name='availableFrom'
              label={t('input.label.availableFrom')}
              placeholder={t('input.placeholder.availableFrom')}
              type='datetime-local'
            />
            {values.withEndDate ? (
              <div className='flex gap-2 w-full'>
                <FormInput.Text
                  name='availableTo'
                  type='datetime-local'
                  label={t('input.label.availableTo')}
                  min={values.availableFrom}
                />
                <Button
                  isClear
                  onlyIcon
                  disabled={status?.displayMode}
                  onClick={() => {
                    setFieldValue('withEndDate', false);
                    setFieldValue('availableTo', '');
                  }}>
                  <Button.Icon icon={<PiCalendarMinus />} />
                </Button>
              </div>
            ) : (
              <Button
                isClear
                fullWidth
                disabled={status?.displayMode}
                onClick={() => setFieldValue('withEndDate', true)}>
                <Button.Text text={t('input.label.availableTo')} />
                <Button.Icon icon={<PiCalendarPlus />} />
              </Button>
            )}
          </Container.Content>
        </Container>
        <Container className='self-start w-full max-w-[350px]'>
          <Container.Header
            title={
              <>
                <PiDeviceMobile /> {t('input.label.clientModuleAccess')}
              </>
            }>
            <FormInput.Checkbox
              name='clientModuleAccess'
              label={
                values.clientModuleAccess
                  ? t('input.label.enabled')
                  : t('input.label.disabled')
              }
            />
          </Container.Header>
          {values.clientModuleAccess && (
            <Container.Content>
              <FormInput.Select
                name='clientAccessModeId'
                label={t('input.label.clientAccessModeId')}
                options={getSelectOptions(
                  CLIENT_ACCESS_MODE,
                  'enum.CLIENT_ACCESS_MODE'
                )}
              />
            </Container.Content>
          )}
        </Container>
        <Container className='self-start w-full max-w-[350px]'>
          <Container.Header
            title={
              <>
                <PiPackage /> {t('input.label.enableDigitalDelivery')}
              </>
            }>
            <FormInput.Checkbox
              name='enableDigitalDelivery'
              label={
                values.enableDigitalDelivery
                  ? t('input.label.enabled')
                  : t('input.label.disabled')
              }
            />
          </Container.Header>
          {values.enableDigitalDelivery && (
            <Container.Content>
              <FormInput.Text
                type='number'
                name='deliveryCode'
                label={t('input.label.deliveryCode')}
                disabled={!values.enableDigitalDelivery}
              />
            </Container.Content>
          )}
        </Container>
        <Container className='self-start w-full max-w-[350px]'>
          <Container.Header
            title={
              <>
                <PiTestTube /> {t('input.label.demoModeId')}
              </>
            }>
            <FormInput.Checkbox
              name='isDemo'
              label={
                values.isDemo
                  ? t('input.label.enabled')
                  : t('input.label.disabled')
              }
            />
          </Container.Header>
          {values.isDemo && (
            <Container.Content>
              <FormInput.Select
                name='demoModeId'
                label={t('input.label.demoModeId')}
                options={getSelectOptions(DEMO_MODE, 'enum.DEMO_MODE')}
                isClearable={false}
                isDisabled={!values.isDemo}
              />
            </Container.Content>
          )}
        </Container>
      </div>
      <Container className='self-start w-full max-w-[350px]'>
        <Container.Header
          title={
            <>
              <PiGear /> {t('label.settings.header')}
            </>
          }
        />
        <Container.Content>
          <div className='flex flex-col w-full gap-3'>
            <FormInput.Select
              name='defaultCurrencyId'
              label={t('input.label.defaultCurrencyId')}
              options={getSelectOptions(CURRENCY, 'enum.CURRENCY')}
            />

            <FormInput.Select
              name='isrcGenerationMode'
              label={t('input.label.isrcGenerationMode')}
              placeholder={t('input.placeholder.isrcGenerationMode')}
              options={[
                { label: 'Generative', value: ISRC_GENERATION_MODE.generative },
                { label: 'Pool', value: ISRC_GENERATION_MODE.pool },
              ]}
              isClearable={false}
            />
            <FormInput.Checkbox
              name='showOwnersLogoAsMain'
              label={t('input.label.showOwnersLogoAsMain')}
            />
          </div>
        </Container.Content>
      </Container>
    </div>
  );
};

export default LabelSettings;
